import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { showPopup } from '../../common/popups/actions'
import * as notifs from '../../common/lib/notifs'
import {
  clearBulkOrderCart,
  dispatchAsync,
  selectCart,
  selectCartLineCount,
  selectHasCartSaveError,
  selectIsCartEditable,
  selectIsCartEmpty,
  selectIsSubmittingBulkOrder,
  submitBulkOrder,
} from '../../common/bulkOrder'
import { Link } from '../components'
import { useBulkOrderCartSyncContext } from './BulkOrderPageShell'

const useBulkOrderCartActions = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const store = useStore()
  const navigate = useNavigate()
  const { flushCartSave } = useBulkOrderCartSyncContext()
  const cartLineCount = useSelector(selectCartLineCount)
  const isCartEmpty = useSelector(selectIsCartEmpty)
  const isCartEditable = useSelector(selectIsCartEditable)
  const isSubmitting = useSelector(selectIsSubmittingBulkOrder)

  const handleEmptyCart = useCallback(() => {
    if (!isCartEditable || isSubmitting) return
    dispatch(
      showPopup('confirm', {
        title: t('Empty cart?'),
        text: t('BULK_ORDER_EMPTY_CART_WARNING'),
        okText: t('empty cart'),
        cancelText: t('cancel'),
        hideCancel: false,
        onConfirm: async () => {
          dispatch(clearBulkOrderCart())
          await flushCartSave()
        },
      }),
    )
  }, [dispatch, flushCartSave, isCartEditable, isSubmitting, t])

  const handleSend = useCallback(() => {
    if (!isCartEditable || isSubmitting) return
    dispatch(
      showPopup('confirm', {
        title: t('Send bulk order?'),
        text: t('BULK_ORDER_SEND_WARNING'),
        okText: t('send'),
        cancelText: t('cancel'),
        hideCancel: false,
        onConfirm: async () => {
          try {
            // Persist draft first; abort if flush fails (incl. conflict rebase).
            const flushed = await flushCartSave()
            if (!flushed) {
              const state = store.getState()
              const cart = selectCart(state)
              const conflictRebase =
                !cart?.dirty && !selectHasCartSaveError(state)
              notifs.error({
                title: t(
                  conflictRebase
                    ? 'BULK_ORDER_CART_CONFLICT_REVIEW'
                    : 'BULK_ORDER_SUBMIT_FAILED',
                ),
              })
              return
            }
            await dispatchAsync(dispatch, submitBulkOrder())
            notifs.success({ title: t('BULK_ORDER_SUBMIT_SUCCESS') })
            navigate(Link.BULK_ORDERS)
          } catch (error) {
            notifs.error({ title: t('BULK_ORDER_SUBMIT_FAILED') })
          }
        },
      }),
    )
  }, [dispatch, flushCartSave, isCartEditable, isSubmitting, navigate, store, t])

  return {
    handleEmptyCart,
    handleSend,
    isEmptyCartDisabled: isCartEmpty || !isCartEditable || isSubmitting,
    isSendDisabled: cartLineCount === 0 || !isCartEditable || isSubmitting,
  }
}

export default useBulkOrderCartActions
